import {
  Label,
  LeftPart,
  ModelNameLabel,
  RightPart,
  Container,
  OrderPerModelLabel,
  IconWrapper,
  ToggleWrapper,
  ToggleGroup,
} from './ModelCompanyRow.styled';
import { useTranslation } from 'react-i18next';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import { useEffect } from 'react';
import { queryClient } from 'index';
import { ReactQueryKeys } from 'services/api/reactQueryKeys';
import Icon from 'components/Icon/Icon';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { Trash } from '@phosphor-icons/react';
import { COLORS } from 'assets/styled';
import {
  useToggleOrderModelExternallyCompany,
  useToggleOrderModelInternallyCompany,
} from './hooks';
import { IModelCompany } from 'types/Model.types';

interface IModelCompanyRowProps {
  modelCompany: IModelCompany;
  withoutLine: boolean;
  isLoadingParent: boolean;
  onDelete?: (id: number) => void;
  isPublic: boolean;
}

const ModelCompanyRow = ({
  modelCompany,
  withoutLine,
  isLoadingParent,
  onDelete,
  isPublic,
}: IModelCompanyRowProps) => {
  const { t } = useTranslation();
  const { isMidTablet, isSmallTablet } = useBreakpointFlag();

  const {
    mutate: toggleOrderInternally,
    isLoading: isLoadingToggleOrderInternally,
    isSuccess: isSuccessToggleOrderInternally,
    reset: resetToggleOrderInternally,
  } = useToggleOrderModelInternallyCompany();

  const {
    mutate: toggleOrderExternally,
    isLoading: isLoadingToggleOrderExternally,
    isSuccess: isSuccessToggleOrderExternally,
    reset: resetToggleOrderExternally,
  } = useToggleOrderModelExternallyCompany();

  const isLoading =
    isLoadingToggleOrderInternally ||
    isLoadingToggleOrderExternally ||
    isLoadingParent;

  useEffect(() => {
    if (isSuccessToggleOrderInternally || isSuccessToggleOrderExternally) {
      queryClient.invalidateQueries([ReactQueryKeys.GET_COMPANIES_FOR_MODEL]);
      resetToggleOrderInternally();
      resetToggleOrderExternally();
    }
  }, [isSuccessToggleOrderInternally, isSuccessToggleOrderExternally]);

  return (
    <Container withoutLine={withoutLine}>
      <LeftPart>
        {modelCompany.company_name && (
          <ModelNameLabel>{modelCompany.company_name}</ModelNameLabel>
        )}
        <Label>#{modelCompany.customer_number}</Label>
        {isSmallTablet && onDelete && !isPublic && (
          <IconWrapper>
            <Icon
              onClick={() => onDelete?.(modelCompany.id)}
              svg={Trash}
              color={COLORS.RED_100}
            />
          </IconWrapper>
        )}
      </LeftPart>
      <RightPart>
        <ToggleWrapper>
          <ToggleGroup>
            <OrderPerModelLabel>{t('Internal Ordering')}</OrderPerModelLabel>
            <ToggleSwitch
              disabled={isLoading}
              isChecked={modelCompany.can_order_internally}
              onClick={() => {
                if (isLoading) {
                  return;
                }
                if (modelCompany.can_order_internally) {
                  toggleOrderInternally({
                    modelId: modelCompany.id,
                    canOrderInternally: false,
                  });
                } else {
                  toggleOrderInternally({
                    modelId: modelCompany.id,
                    canOrderInternally: true,
                  });
                }
              }}
              size={isMidTablet ? 'small' : 'large'}
            />
          </ToggleGroup>
          <ToggleGroup>
            <OrderPerModelLabel>{t('External Ordering')}</OrderPerModelLabel>
            <ToggleSwitch
              disabled={isLoading}
              isChecked={modelCompany.can_order_externally}
              onClick={() => {
                if (isLoading) {
                  return;
                }
                if (modelCompany.can_order_externally) {
                  toggleOrderExternally({
                    modelId: modelCompany.id,
                    canOrderExternally: false,
                  });
                } else {
                  toggleOrderExternally({
                    modelId: modelCompany.id,
                    canOrderExternally: true,
                  });
                }
              }}
              size={isMidTablet ? 'small' : 'large'}
            />
          </ToggleGroup>
        </ToggleWrapper>
        {!isSmallTablet && onDelete && !isPublic && (
          <Icon
            onClick={() => onDelete?.(modelCompany.id)}
            svg={Trash}
            color={COLORS.RED_100}
          />
        )}
      </RightPart>
    </Container>
  );
};

export default ModelCompanyRow;
